import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AssignmentOutlined,
  CampaignOutlined,
  EventAvailableOutlined,
  FactCheckOutlined,
  FeedOutlined,
  InsightsOutlined,
  LocalDiningOutlined,
  ScheduleOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const getClassDisplayName = (cls) => {
  if (!cls) return 'Class';
  if (typeof cls.classDisplayName === 'string' && cls.classDisplayName.trim()) {
    return cls.classDisplayName;
  }
  if (typeof cls.displayName === 'string' && cls.displayName.trim()) {
    return cls.displayName;
  }
  if (cls.name && cls.stream) return `${cls.name}${cls.stream}`;
  return cls.name || 'Class';
};

const featureCards = [
  {
    title: 'Clock In / Clock Out',
    description: 'Use browser GPS to record daily teacher attendance.',
    icon: EventAvailableOutlined,
    color: '#2563eb',
    path: '/teacher-portal/clock',
  },
  {
    title: 'Student Attendance',
    description: 'Mark weekly present and absent records for assigned classes.',
    icon: FactCheckOutlined,
    color: '#059669',
    path: '/teacher-portal/student-attendance',
  },
  {
    title: 'Exams and Report',
    description: 'Enter SBA, exams, comments, promotion, and report details.',
    icon: InsightsOutlined,
    color: '#7c3aed',
    path: '/teacher-portal/exams-report',
  },
  {
    title: 'Feeding Fee',
    description: 'Mark daily feeding payments for students in class.',
    icon: LocalDiningOutlined,
    color: '#ea580c',
    path: '/teacher-portal/feeding-fee',
  },
  {
    title: 'Assignments',
    description: 'Create and review class assignments from the browser.',
    icon: AssignmentOutlined,
    color: '#0f766e',
    path: '/teacher-portal/assignments',
  },
  {
    title: 'Announcements',
    description: 'Read and send school or class announcements.',
    icon: CampaignOutlined,
    color: '#be123c',
    path: '/teacher-portal/announcements',
  },
  {
    title: 'Timetable',
    description: 'View teaching timetable and class periods.',
    icon: ScheduleOutlined,
    color: '#4f46e5',
    path: '/teacher-portal/timetable',
  },
  {
    title: 'Weekly Exercises',
    description: 'Record exercise checks and classroom activity logs.',
    icon: FeedOutlined,
    color: '#64748b',
    path: '/teacher-portal/exercises',
  },
];

const FEATURE_CARD_HEIGHT = 252;

export default function TeacherPortalHome() {
  const theme = useTheme();
  const navigate = useNavigate();
  const teacher = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const assignedClasses = Array.isArray(teacher?.assignedClasses)
    ? teacher.assignedClasses
    : [];

  return (
    <Stack spacing={3}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={3}
          >
            <Box>
              <Typography variant="overline" color="primary" fontWeight={800}>
                Browser fallback
              </Typography>
              <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5 }}>
                Welcome, {teacher?.name || 'Teacher'}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
                This teacher-only portal is for phones that cannot run the
                Android app. It will use the same teacher account and backend
                records as the mobile app.
              </Typography>
            </Box>

            <Box
              sx={{
                minWidth: { xs: '100%', md: 280 },
                p: 2,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={800}>
                Assigned Classes
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {assignedClasses.length > 0 ? (
                  assignedClasses.slice(0, 6).map((cls) => (
                    <Chip
                      key={cls._id || cls.id || getClassDisplayName(cls)}
                      label={getClassDisplayName(cls)}
                      size="small"
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No assigned classes found.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Box>
        <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>
          Teacher Features
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Priority modules are the first mobile features to bring into the web
          portal.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          {featureCards.map((feature) => {
            const Icon = feature.icon;

            return (
              <Box key={feature.title} sx={{ minWidth: 0 }}>
                <Card
                  sx={{
                    width: '100%',
                    height: FEATURE_CARD_HEIGHT,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'border-color 160ms ease, transform 160ms ease',
                    '&:hover': {
                      borderColor: alpha(feature.color, 0.55),
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      p: 2.25,
                      boxSizing: 'border-box',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      sx={{ minHeight: 48 }}
                    >
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: 1,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: alpha(feature.color, 0.12),
                          color: feature.color,
                        }}
                      >
                        <Icon />
                      </Box>
                    </Stack>

                    <Box sx={{ flex: 1, minHeight: 112 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={900}
                        sx={{
                          minHeight: 48,
                          lineHeight: 1.25,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.75,
                          minHeight: 62,
                          lineHeight: 1.45,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => navigate(feature.path)}
                      sx={{ textTransform: 'none', borderRadius: 1, mt: 'auto' }}
                    >
                      Open
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Stack>
  );
}
